import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { PlusCircle, FilterIcon, Search, X } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import PersonalRegistroModal from './components/PersonalRegistroModal';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import { useToast } from '@/components/ui/use-toast';

// Se reutilizan las definiciones de columna y los servicios de la
// parametrización de empleados: el módulo de Personal no redefine reglas,
// consume el mismo maestro `employee`. Lo único propio de esta vista es que
// es de toda la empresa (el modo por centro de lavado sigue siendo de
// Parametrización) y que no borra: el borrado sigue en Parametrización,
// porque es destructivo y conviene que viva en un solo lugar.
import { employeesColumns, columnsExcel } from '@/pages/parametrizacion/employees/utils/employeesColumns';
import {
  fetchEmployees,
  createEmployee,
  updateEmployee,
  exportEmployees,
} from '@/pages/parametrizacion/employees/Services/employees.services';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

// Se quita la acción de eliminar sin mutar el arreglo compartido que usa la
// página de Parametrización.
const columnasListado = employeesColumns.map((col) =>
  col.id === 'actions'
    ? { ...col, actions: (col.actions || []).filter((a) => a.key === 'edit') }
    : col
);

// Escapa los caracteres que MongoDB interpretaría como metacaracteres de
// regex, para que buscar "(", "." o "+" no rompa la consulta ni devuelva
// resultados inesperados.
const escapeRegex = (texto) => texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const PersonalEmpleadosPage = () => {
  const { toast } = useToast();

  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [laundries, setLaundries] = useState([]);

  // Búsqueda rápida por nombre. Se escribe en `searchTerm` y se aplica a
  // `busqueda` con retardo, para no pedir una consulta por cada tecla.
  const [searchTerm, setSearchTerm] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [saving, setSaving] = useState(false);

  // La búsqueda va al servidor: filtra sobre todo el padrón, no solo las
  // filas de la página actual. `employee/filter` pasa el query directo a
  // find(), así que un $or con $regex funciona sin tocar el backend.
  const consulta = useMemo(() => {
    const base = { ...filters };
    const termino = busqueda.trim();
    if (termino) {
      const coincide = { $regex: escapeRegex(termino), $options: 'i' };
      base.$or = [{ nombre: coincide }, { apellido: coincide }];
    }
    return base;
  }, [filters, busqueda]);

  const fetchAndSetEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, totalRecords } = await fetchEmployees({
        limit: ITEMS_PER_PAGE,
        offset: currentPage,
        query: consulta,
      });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron obtener los empleados.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, consulta, toast]);

  useEffect(() => {
    fetchAndSetEmployees();
  }, [fetchAndSetEmployees]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setBusqueda(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchLaundriesData = async () => {
      try {
        const response = await fetchLaundries({ limit: 500, offset: 1, query: { estado: 'Activo' } });
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.items)
            ? response.items
            : [];
        setLaundries(list.map((laundry) => ({
          value: laundry._id || laundry.id,
          label: laundry.nombre,
        })));
      } catch (error) {
        toast({
          title: 'Error',
          description: 'No se pudieron obtener los centros de lavado.',
          variant: 'destructive',
        });
      }
    };
    fetchLaundriesData();
  }, [toast]);

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (itemData) => {
    setSaving(true);
    try {
      if (modalMode === 'create') {
        await createEmployee(itemData);
        toast({ title: 'Éxito', description: 'Empleado creado correctamente.' });
      } else if (modalMode === 'edit') {
        await updateEmployee(currentItem._id, itemData);
        toast({ title: 'Éxito', description: 'Empleado actualizado correctamente.' });
      }
      handleCloseModal();
      fetchAndSetEmployees();
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          error.message ||
          'Ocurrió un problema al guardar el empleado.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAction = (actionType, row) => {
    if (actionType === 'edit') {
      handleOpenModal('edit', row);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setIsDrawerOpen(false);
  };

  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card>
        <CardHeader className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap gap-2 justify-end">
              <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o apellido"
              aria-label="Buscar por nombre o apellido"
              className="pl-9 pr-9"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                aria-label="Limpiar búsqueda"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
            <ExportExcelButton
              fileName="Personal"
              columns={columnsExcel}
              filters={consulta}
              service={exportEmployees}
            />
            <Button onClick={() => handleOpenModal('create')}>
              <PlusCircle className="mr-2 h-4 w-4" /> Registrar empleado
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsDrawerOpen(true)}
              className="gap-2"
            >
              <FilterIcon className="mr-2 h-4 w-4" variant="outline" size={20} />
              Filtros
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="w-full">
            <AppliedFilters
              filters={filters}
              fields={[
                {
                  id: 'estado',
                  label: 'Estado',
                  type: 'select',
                  options: [
                    { value: 'Activo', label: 'Activo' },
                    { value: 'Inactivo', label: 'Inactivo' },
                  ],
                },
                {
                  id: 'rol',
                  label: 'Rol',
                  type: 'select',
                  options: [
                    { value: 'Administrador', label: 'Administrador' },
                    { value: 'Asesor comercial', label: 'Asesor comercial' },
                    { value: 'Supervisor planta', label: 'Supervisor planta' },
                    { value: 'Auxiliar lavanderia', label: 'Auxiliar lavanderia' },
                    { value: 'Conductor', label: 'Conductor' },
                  ],
                },
                {
                  id: 'tipoNomina',
                  label: 'Tipo de Nómina',
                  type: 'select',
                  options: [
                    { value: 'Fijo', label: 'Fijo' },
                    { value: 'Temporal', label: 'Temporal' },
                  ],
                },
                { id: 'lavanderia.id', label: 'Centro de Lavado', type: 'select', options: laundries },
              ]}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>

          <DataTable
            data={displayedData}
            columns={columnasListado}
            isLoading={isLoading}
            onAction={handleAction}
            showCreateButton={false}
          />

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </CardContent>
      </Card>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={[
          {
            id: 'estado',
            label: 'Estado',
            type: 'select',
            options: [
              { value: 'Activo', label: 'Activo' },
              { value: 'Inactivo', label: 'Inactivo' },
            ],
          },
          {
            id: 'rol',
            label: 'Rol',
            type: 'select',
            options: [
              { value: 'Administrador', label: 'Administrador' },
              { value: 'Asesor comercial', label: 'Asesor comercial' },
              { value: 'Supervisor planta', label: 'Supervisor planta' },
              { value: 'Auxiliar lavanderia', label: 'Auxiliar lavanderia' },
              { value: 'Conductor', label: 'Conductor' },
            ],
          },
          {
            id: 'tipoNomina',
            label: 'Tipo de Nómina',
            type: 'select',
            options: [
              { value: 'Fijo', label: 'Fijo' },
              { value: 'Temporal', label: 'Temporal' },
            ],
          },
          { id: 'lavanderia.id', label: 'Centro de Lavado', type: 'select', options: laundries },
        ]}
        initialFilters={filters}
        onChange={handleFilterChange}
        onApply={handleFilterChange}
      />

      <PersonalRegistroModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveItem}
        item={currentItem}
        laundries={laundries}
        saving={saving}
      />
    </motion.div>
  );
};

export default PersonalEmpleadosPage;